import { useMutation } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as AuthApi from '@/api/auth';
import { ApiHttpError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ChangePasswordCard() {
  const { t } = useTranslation(['settings', 'errors']);
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: AuthApi.changePassword,
    onSuccess: () => {
      setOkMsg(t('settings:password.successToast'));
      setTimeout(() => navigate('/login'), 1_500);
    },
    onError: (err) => {
      if (err instanceof ApiHttpError && err.status === 401) {
        setErrMsg(t('settings:password.errors.wrongCurrent'));
      } else {
        setErrMsg(t('errors:unknown'));
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrMsg(null);
    setOkMsg(null);
    if (next !== confirm) {
      setErrMsg(t('settings:password.errors.mismatch'));
      return;
    }
    if (next.length < 8) {
      setErrMsg(t('settings:password.errors.tooShort'));
      return;
    }
    mutation.mutate({ current_password: current, new_password: next });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings:password.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <Label htmlFor="cp-current">{t('settings:password.current')}</Label>
            <Input
              id="cp-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <Label htmlFor="cp-new">{t('settings:password.new')}</Label>
            <Input
              id="cp-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <div>
            <Label htmlFor="cp-confirm">{t('settings:password.confirm')}</Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          {errMsg && (
            <div role="alert" className="text-sm text-destructive">
              {errMsg}
            </div>
          )}
          {okMsg && <div className="text-sm text-emerald-700">{okMsg}</div>}
          <Button type="submit" size="sm" disabled={mutation.isPending}>
            {mutation.isPending
              ? t('settings:password.submitting')
              : t('settings:password.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
